import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from "@angular/core";
import { UIRouterGlobals } from "@uirouter/core";
import { forkJoin } from "rxjs";
import { QueriesEnvironmentsService } from "../../models/queries-environments.service";
import { QueuesEnvironmentsService } from "../../models/queues-environments.service";
import { RoutesEnvironmentsService } from "../../models/routes-environments.service";
import { RememberService } from "../../remember/remember.service";
import { RoundTimeService } from "../../round-time/round-time.service";
import { TimeDurAgoService } from "../../time-dur-ago/time-dur-ago.service";

@Component({
  selector: "ab-environment-switch",
  templateUrl: "./environment-switch.component.html",
})
export class EnvironmentSwitchComponent implements OnChanges {
  @Input() public featureDisabled: boolean;
  @Input() public project: any;
  @Output() public changed = new EventEmitter<any>();

  public environment: string;
  public environments: string[];

  // Max number of environments where we allow "All environments" filter.
  private MAX_ENVS = 4;
  private REMEMBER_ENVIRONMENT: string;

  constructor(
    private $uiRouterGlobals: UIRouterGlobals,
    private rememberService: RememberService,
    private roundTimeService: RoundTimeService,
    private routesEnvironmentsService: RoutesEnvironmentsService,
    private queriesEnvironmentsService: QueriesEnvironmentsService,
    private queuesEnvironmentsService: QueuesEnvironmentsService,
    private timeDurAgoService: TimeDurAgoService,
  ) {
    this.REMEMBER_ENVIRONMENT = `projectPerformance.${$uiRouterGlobals.params.projectId}.environment`;
  }

  public ngOnChanges() {
    if (this.featureDisabled == null) {
      return;
    }

    if (this.featureDisabled) {
      this.changeEnvironment(undefined, false);
      delete this.environments;
      return;
    }

    const params = {
      projectId: this.project.id,
      time__gte: this.roundTimeService.timestampFor(
        this.timeDurAgoService.timeFor("90d/1d", 90),
        "90d/1d",
      ),
    };

    forkJoin([
      this.routesEnvironmentsService.get(params),
      this.queriesEnvironmentsService.get(params),
      this.queuesEnvironmentsService.get(params),
    ]).subscribe((allEnvs: any) => {
      const environments = [];
      for (const envs of allEnvs) {
        for (const e of envs) {
          if (environments.indexOf(e.environment) > -1) {
            continue;
          }
          environments.push(e.environment);
        }
      }

      if (environments.length === 0) {
        this.changeEnvironment(undefined, false);
        delete this.environments;
        return;
      }

      if (environments.length === 1) {
        this.changeEnvironment(environments[0], false);
        delete this.environments;
        return;
      }

      const envUrlParam = this.$uiRouterGlobals.params.environment;
      let env =
        envUrlParam !== undefined
          ? envUrlParam
          : this.rememberService.get(this.REMEMBER_ENVIRONMENT);
      if (this.isInvalidEnvironment(env, environments)) {
        env = this.defaultEnvironment(allEnvs[0]);
      }

      this.changeEnvironment(env, false);
      this.environments = environments;
    });
  }

  public trackByIndex(index: number, _item: any) {
    return index;
  }

  public changeEnvironment(environment: any, remember: boolean) {
    this.environment = environment;
    this.changed.emit(environment);

    if (remember) {
      this.rememberService.set(this.REMEMBER_ENVIRONMENT, environment);
    }
  }

  public showAllEnvironmentsOption() {
    return (
      this.environment !== "all" && this.environments.length <= this.MAX_ENVS
    );
  }

  public selectedEnvironmentName() {
    if (this.environment === "") {
      return "Unknown environment";
    }
    if (this.environment === "all") {
      return "All environments";
    }

    return this.environment;
  }

  // Default to 'production' environment if present, otherwise use first
  // available environment.
  private defaultEnvironment(envs: any) {
    if (envs.map((e: any) => e.environment).indexOf("production") > -1) {
      return "production";
    }

    return envs[0].environment;
  }

  private isInvalidEnvironment(env: any, envs: any) {
    if (env === undefined) {
      return true;
    }
    if (env === "all" && envs.length > this.MAX_ENVS) {
      return true;
    }
    if (env !== "all" && envs.indexOf(env) < 0) {
      return true;
    }

    return false;
  }
}
